import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Paper,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  PlayCircleOutline as ReactivateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { RootState } from '../app/store';
import { PlansMultiSelect } from './PlansMulti';
import { AddonsMultiSelect } from './AddonsMulti';
import { fetchPlans } from '../features/plans/plans';
import { fetchAddons } from '../features/addons/addons';

// ---- Types matching backend JSON (snake_case) ----
export type CouponKind = 'plan' | 'addon';

export type Coupon = {
  status: 'inactive' | 'active' | 'unknown' | 'deleted' | 'internal';
  promo_code: string;
  created_at?: string; // ISO
  updated_at?: string; // ISO
  deleted_at?: string; // ISO or empty
  duration?: number;
  expiry_date?: string; // ISO
  internal_coupon_name?: string;
  invoice_name?: string;
  campaign?: string;
  collateral?: string;
  linked_to_url?: string;
  notes?: string;
  dollar_amount?: number;
  percentage?: number;
  plan_ids: Array<string>;
  addon_ids: Array<string>;
  // Backend-computed deep link to the Chargebee admin. Optional for
  // robustness if the backend wasn't configured with a site.
  chargebee_url?: string;
};

const API_BASE = '/api/v1/admin/coupons';

const couponKind = (c: Coupon): CouponKind => ((c.addon_ids ?? []).length > 0 ? 'addon' : 'plan');

const emptyCoupon = (): Coupon => ({
  status: 'unknown',
  plan_ids: [],
  addon_ids: [],
  promo_code: '',
  duration: 1,
});

// ---- Helpers ----
async function fetchJSON<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

function formatWhen(v?: string) {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

// The datetime-local (and date) inputs produce a wall-clock value in the
// browser's local timezone, e.g. "2026-01-19T23:59". Interpret it as local time
// and convert to a true UTC RFC3339 instant for the backend. Appending "Z"
// (the previous behaviour) mislabelled local time as UTC, shifting the stored
// value by the local offset (+10h in AEST).
function toISOStringFromLocalDateOrDT(value: string | undefined): string | undefined {
  if (!value) return undefined;
  // Normalise to a form JS parses as *local* time (a bare date is otherwise
  // parsed as UTC midnight, and a value without seconds still parses as local).
  let local = value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) local = `${value}T00:00:00`;
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) local = `${value}:00`;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

// Convert a stored value into the "YYYY-MM-DDTHH:mm" wall-clock string a
// datetime-local input expects, in the browser's local timezone. Handles both a
// UTC ISO string (from a loaded coupon) and a local input string (mid-edit).
function toLocalDateTimeInput(value: string | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---- Dialog form component ----
const CouponDialog = ({
  open,
  mode,
  kind,
  /* eslint-disable-next-line react/require-default-props */
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  // The "kind" of coupon being authored. On create, comes from the active tab.
  // On edit, comes from the existing coupon — the user cannot flip kind.
  kind: CouponKind;
  initial?: Coupon;
  onClose: () => void;
  onSaved: (saved: Coupon) => void;
}) => {
  const [form, setForm] = useState<Coupon>(initial ?? emptyCoupon());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const plansState = useAppSelector((state: RootState) => state.plans);
  const addonsState = useAppSelector((state: RootState) => state.addons);

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchAddons());
  }, [dispatch]);

  useEffect(() => {
    setForm(
      initial
        ? { ...initial, plan_ids: initial.plan_ids ?? [], addon_ids: initial.addon_ids ?? [] }
        : emptyCoupon(),
    );
  }, [initial, open]);

  const isEdit = mode === 'edit';

  const handleChange = (key: keyof Coupon) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    const toNum = (v: string) => (v === '' ? undefined : Number(v));
    setForm((f) => ({
      ...f,
      [key]:
       key === 'dollar_amount' || key === 'percentage' || key === 'duration'
         ? toNum(value)
         : value,
    }));
  };

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      // Force the unused id-array empty so we always submit a clean payload
      // matching the active kind. Backend rejects the mixed case, but it's
      // friendlier to never send it.
      const payload: Coupon = {
        ...form,
        expiry_date: toISOStringFromLocalDateOrDT(form.expiry_date),
        plan_ids: kind === 'plan' ? (form.plan_ids ?? []).map(String) : [],
        addon_ids: kind === 'addon' ? (form.addon_ids ?? []).map(String) : [],
      };
      let saved: Coupon;
      if (isEdit) {
        if (!form.promo_code) throw new Error('promo_code missing');
        saved = await fetchJSON<Coupon>(`${API_BASE}/${encodeURIComponent(form.promo_code)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        if (!form.promo_code) throw new Error('promo_code is required');
        saved = await fetchJSON<Coupon>(`${API_BASE.replace(/\/$/, '')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      onSaved(saved);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  const titleSuffix = kind === 'addon' ? 'addon coupon' : 'plan coupon';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? `Update ${titleSuffix}` : `Create ${titleSuffix}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Promo code"
              value={form.promo_code ?? ''}
              onChange={handleChange('promo_code')}
              InputProps={{ readOnly: isEdit }}
              required
              fullWidth
            />
            <TextField label="Campaign" value={form.campaign ?? ''} onChange={handleChange('campaign')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Status"
              value={form.status ?? 'unknown'}
              fullWidth
              onChange={(e) => {
                setForm((f) => ({ ...f, status: e.target.value as Coupon['status'] }));
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
            </TextField>

            {kind === 'plan' ? (
              <PlansMultiSelect
                plansState={plansState.value.filter((p) => p.published)}
                value={form.plan_ids ?? []}
                onChange={(ids) => setForm((f) => ({ ...f, plan_ids: ids }))}
              />
            ) : (
              <AddonsMultiSelect
                addonsState={addonsState.value}
                value={form.addon_ids ?? []}
                onChange={(ids) => setForm((f) => ({ ...f, addon_ids: ids }))}
              />
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Internal name" value={form.internal_coupon_name ?? ''} onChange={handleChange('internal_coupon_name')} fullWidth />
            <TextField label="Invoice name" value={form.invoice_name ?? ''} onChange={handleChange('invoice_name')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Dollar amount" type="number" value={form.dollar_amount ?? ''} onChange={handleChange('dollar_amount')} fullWidth />
            <TextField label="Percentage %" type="number" value={form.percentage ?? ''} onChange={handleChange('percentage')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Expiry date & time (local)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={toLocalDateTimeInput(form.expiry_date)}
              onChange={handleChange('expiry_date')}
              fullWidth
            />
            <TextField
              label="Duration (months the coupon is applied)"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={form.duration ?? 1}
              onChange={handleChange('duration')}
              helperText="0 = one-time; 1, 2, … = number of months"
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Collateral" value={form.collateral ?? ''} onChange={handleChange('collateral')} fullWidth />
            <TextField label="Linked URL" value={form.linked_to_url ?? ''} onChange={handleChange('linked_to_url')} fullWidth />
          </Stack>

          <TextField label="Notes" value={form.notes ?? ''} onChange={handleChange('notes')} fullWidth multiline minRows={3} />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* eslint-disable-next-line react/jsx-no-bind */}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {/* eslint-disable-next-line react/jsx-no-bind */}
        <Button variant="contained" onClick={handleSubmit} disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>
          {isEdit ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Reactivate dialog ----
// Reactivation is stateful in Chargebee (unarchive vs. moving the expiry), so
// it goes through a dedicated endpoint. We ask the operator when the coupon
// should expire: a specific date, or never.
const ReactivateDialog = ({
  open,
  promoCode,
  onClose,
  onDone,
}: {
  open: boolean;
  promoCode: string | null;
  onClose: () => void;
  onDone: (code: string) => void;
}) => {
  const [neverExpires, setNeverExpires] = useState(true);
  const [expiry, setExpiry] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNeverExpires(true);
    setExpiry('');
    setError(null);
    setSaving(false);
  }, [open, promoCode]);

  async function handleReactivate() {
    if (!promoCode) return;
    setSaving(true);
    setError(null);
    try {
      const payload: { expiry_date?: string } = {};
      if (!neverExpires) {
        if (!expiry) throw new Error('Pick an expiry date & time, or choose "Never expires".');
        payload.expiry_date = toISOStringFromLocalDateOrDT(expiry);
      }
      await fetchJSON(`${API_BASE}/${encodeURIComponent(promoCode)}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      onDone(promoCode);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{`Reactivate coupon ${promoCode ?? ''}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            This will make the coupon active again in Chargebee. Choose when it should expire.
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={neverExpires}
                onChange={(e) => setNeverExpires(e.target.checked)}
              />
            )}
            label="Never expires"
          />
          <TextField
            label="Expiry date & time (local)"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            disabled={neverExpires}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* eslint-disable-next-line react/jsx-no-bind */}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          /* eslint-disable-next-line react/jsx-no-bind */
          onClick={handleReactivate}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          Reactivate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CouponsTable = ({
  loading,
  rows,
  onEdit,
  onDelete,
  onReactivate,
}: {
  loading: boolean;
  rows: Coupon[];
  onEdit: (promoCode: string) => void;
  onDelete: (promoCode: string) => void;
  onReactivate: (promoCode: string) => void;
}) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Promo code</TableCell>
          <TableCell>Internal name</TableCell>
          <TableCell>Campaign</TableCell>
          <TableCell align="right">$ Amount</TableCell>
          <TableCell align="right">% Off</TableCell>
          <TableCell>Expiry</TableCell>
          <TableCell>Updated</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && (
          <TableRow>
            <TableCell colSpan={9} align="center">
              <CircularProgress size={22} />
            </TableCell>
          </TableRow>
        )}
        {!loading && rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary' }}>
              No coupons yet
            </TableCell>
          </TableRow>
        )}
        {!loading && rows.map((row) => (
          /* eslint-disable-next-line react/jsx-no-bind */
          <TableRow
            hover
            key={row.promo_code}
            /* eslint-disable-next-line react/jsx-no-bind */
            onClick={() => onEdit(row.promo_code)}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell>{row.promo_code}</TableCell>
            <TableCell>{row.internal_coupon_name}</TableCell>
            <TableCell>{row.campaign}</TableCell>
            <TableCell align="right">{row.dollar_amount ?? ''}</TableCell>
            <TableCell align="right">{row.percentage ?? ''}</TableCell>
            <TableCell>{row.expiry_date ? new Date(row.expiry_date).toLocaleString() : ''}</TableCell>
            <TableCell>{formatWhen(row.updated_at)}</TableCell>
            <TableCell>
              {/* eslint-disable-next-line no-nested-ternary */}
              {row.status === 'active' || row.status === 'internal' ? (
                <Chip size="small" label={row.status} color="success" variant="outlined" />
              ) : row.status === 'deleted' ? (
                <Chip size="small" label="Deleted" color="error" variant="outlined" />
              ) : (
                <Chip size="small" label={row.status} color="warning" variant="outlined" />
              )}
            </TableCell>
            {/* eslint-disable-next-line react/jsx-no-bind */}
            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
              {row.chargebee_url && (
                <IconButton
                  size="small"
                  aria-label="open in Chargebee"
                  title="Open in Chargebee"
                  href={row.chargebee_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
              {row.status === 'inactive' && (
                <IconButton
                  size="small"
                  aria-label="reactivate"
                  title="Reactivate"
                  color="success"
                  /* eslint-disable-next-line react/jsx-no-bind */
                  onClick={() => onReactivate(row.promo_code)}
                >
                  <ReactivateIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                aria-label="edit"
                /* eslint-disable-next-line react/jsx-no-bind */
                onClick={() => onEdit(row.promo_code)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {/* eslint-disable-next-line react/jsx-no-bind */}
              <IconButton size="small" aria-label="delete" onClick={() => onDelete(row.promo_code)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ---- Main page ----
//
/* eslint-disable-next-line react/function-component-definition */
export default function AdminCouponsPage() {
  const [rows, setRows] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const [tab, setTab] = useState<CouponKind>('plan');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [dialogKind, setDialogKind] = useState<CouponKind>('plan');
  const [current, setCurrent] = useState<Coupon | undefined>(undefined);

  const [reactivateFor, setReactivateFor] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchJSON<Coupon[]>(API_BASE);
      // Defensively ensure addon_ids exists for legacy records.
      setRows(list.map((c) => ({ ...c, addon_ids: c.addon_ids ?? [], plan_ids: c.plan_ids ?? [] })));
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setCurrent(undefined);
    setDialogMode('create');
    // create dialog inherits the active tab's kind
    setDialogKind(tab);
    setDialogOpen(true);
  }

  async function openEdit(promoCode: string) {
    try {
      const item = await fetchJSON<Coupon>(`${API_BASE}/${encodeURIComponent(promoCode)}`);
      const normalised: Coupon = { ...item, addon_ids: item.addon_ids ?? [], plan_ids: item.plan_ids ?? [] };
      setCurrent(normalised);
      setDialogMode('edit');
      // edit locks to the existing coupon's kind — no flipping plan↔addon.
      setDialogKind(couponKind(normalised));
      setDialogOpen(true);
    } catch (e: any) {
      setSnack(e.message || String(e));
    }
  }

  async function softDelete(promoCode: string) {
    /* eslint-disable-next-line no-alert, no-restricted-globals */
    if (!confirm(`Soft delete coupon ${promoCode}?`)) return;
    try {
      await fetchJSON(`${API_BASE}/${encodeURIComponent(promoCode)}`, { method: 'DELETE' });
      setSnack('Coupon deleted (soft)');
      await load();
    } catch (e: any) {
      setSnack(e.message || String(e));
    }
  }

  const onReactivated = async (promoCode: string) => {
    setReactivateFor(null);
    setSnack(`Coupon ${promoCode} reactivated`);
    await load();
  };

  const onSaved = (saved: Coupon) => {
    setDialogOpen(false);
    setSnack(dialogMode === 'create' ? 'Coupon created' : 'Coupon updated');
    setRows((prev) => {
      const idx = prev.findIndex((x) => x.promo_code === saved.promo_code);
      const normalised: Coupon = { ...saved, addon_ids: saved.addon_ids ?? [], plan_ids: saved.plan_ids ?? [] };
      if (idx >= 0) {
        const next = prev.slice(); next[idx] = normalised; return next;
      }
      return [normalised, ...prev];
    });
  };

  const visibleRows = useMemo(() => {
    const filtered = rows.filter((c) => couponKind(c) === tab);
    return [...filtered].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  }, [rows, tab]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mr: 15, ml: 15, mt: 10,
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Coupons</Typography>
        <Box>
          {/* eslint-disable-next-line react/jsx-no-bind */}
          <IconButton aria-label="refresh" onClick={load}>
            <RefreshIcon />
          </IconButton>
          {/* eslint-disable-next-line react/jsx-no-bind */}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ml: 1 }}>
            Create
          </Button>
        </Box>
      </Toolbar>

      <Tabs
        value={tab}
        /* eslint-disable-next-line react/jsx-no-bind */
        onChange={(_e, v: CouponKind) => setTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab value="plan" label="Plan coupons" />
        <Tab value="addon" label="Addon coupons" />
      </Tabs>

      <CouponsTable
        loading={loading}
        rows={visibleRows}
        /* eslint-disable-next-line react/jsx-no-bind */
        onEdit={openEdit}
        /* eslint-disable-next-line react/jsx-no-bind */
        onDelete={softDelete}
        /* eslint-disable-next-line react/jsx-no-bind */
        onReactivate={setReactivateFor}
      />

      <ReactivateDialog
        open={reactivateFor !== null}
        promoCode={reactivateFor}
        onClose={() => setReactivateFor(null)}
        onDone={onReactivated}
      />

      <CouponDialog
        open={dialogOpen}
        mode={dialogMode}
        kind={dialogKind}
        initial={current}
        onClose={() => setDialogOpen(false)}
        onSaved={onSaved}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack(null)} severity="success" variant="filled">{snack}</Alert>
      </Snackbar>

      {error && (
        <Box mt={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
    </Paper>
  );
}
